import React, { useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Home, ClipboardList, LineChart, UserCircle, HeartHandshake, Loader2 } from "lucide-react";
import { useClientAuth } from "../hooks/useClientAuth";
import { updateClientSelf } from "../lib/api";
import InstallBanner from "./InstallBanner";

const TZ_SYNC_KEY = "lc_m_tz_v1";

const TABS = [
  { to: "/m/home",     label: "Home",     icon: Home },
  { to: "/m/plan",     label: "My Plan",  icon: ClipboardList },
  { to: "/m/progress", label: "Progress", icon: LineChart },
  { to: "/m/coach",    label: "Coach",    icon: HeartHandshake },
  { to: "/m/profile",  label: "Profile",  icon: UserCircle },
];

export default function MobileShell() {
  const { token, profile, loading } = useClientAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Push the browser's current IANA timezone up to the backend once per session
  // (so the per-member local 09:00 nudge fires at the right time).
  const tzSentRef = React.useRef(false);
  useEffect(() => {
    if (!token || !profile || tzSentRef.current) return;
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!tz) return;
      const sentTz = sessionStorage.getItem(TZ_SYNC_KEY);
      if (sentTz === tz || profile.timezone === tz) {
        tzSentRef.current = true;
        sessionStorage.setItem(TZ_SYNC_KEY, tz);
        return;
      }
      // Mark as sent BEFORE awaiting to prevent a race on profile refresh.
      tzSentRef.current = true;
      sessionStorage.setItem(TZ_SYNC_KEY, tz);
      updateClientSelf(token, { timezone: tz }).catch(() => {
        tzSentRef.current = false;
        sessionStorage.removeItem(TZ_SYNC_KEY);
      });
    } catch {
      /* ignore */
    }
  }, [token, profile]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--lc-cream)]">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--lc-muted)]" />
      </div>
    );
  }

  if (!token || !profile) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/m/login?next=${next}`} replace />;
  }

  return (
    <div
      className="min-h-screen bg-[var(--lc-cream)]"
      style={{
        paddingBottom: "calc(72px + env(safe-area-inset-bottom))",
        paddingTop: "env(safe-area-inset-top)",
      }}
      data-testid="mobile-shell"
    >
      <main className="mx-auto max-w-[480px] px-4 pt-4 pb-6">
        <Outlet context={{ profile, token, navigate }} />
      </main>

      <InstallBanner />

      {/* Bottom navigation */}
      <nav
        className="fixed bottom-0 inset-x-0 bg-[var(--lc-cream)]/95 backdrop-blur-md border-t border-[var(--lc-line)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-nav"
      >
        <ul className="mx-auto max-w-[480px] grid grid-cols-5">
          {TABS.map((t) => (
            <li key={t.to}>
              <NavLink
                to={t.to}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-bold tracking-[0.14em] uppercase transition-colors ${
                    isActive
                      ? "text-[var(--lc-ink)]"
                      : "text-[var(--lc-muted)]/80 hover:text-[var(--lc-ink)]"
                  }`
                }
                data-testid={`mobile-tab-${t.to.split("/").pop()}`}
              >
                {({ isActive }) => (
                  <>
                    <t.icon className="h-5 w-5" strokeWidth={isActive ? 2.4 : 1.8} />
                    <span>{t.label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

export function ScreenHeader({ eyebrow, title, action }) {
  return (
    <header className="mb-4 flex items-start justify-between gap-3">
      <div className="min-w-0">
        {eyebrow && <div className="eyebrow text-[var(--lc-muted)]">{eyebrow}</div>}
        <h1 className="font-serif-display text-[28px] leading-tight mt-1 truncate">{title}</h1>
      </div>
      {action}
    </header>
  );
}

export function Card({ children, className = "", ...props }) {
  return (
    <section
      className={`bg-white border border-[var(--lc-line)] rounded-xl p-4 ${className}`}
      {...props}
    >
      {children}
    </section>
  );
}
